import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  Tooltip,
} from "@mui/material";
import CommonDatePicker from "../datepicker/CommonDatePicker";
import config from "../common/Config";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const OrderLocationReport = () => {
  const accessToken = localStorage.getItem("accessToken");
  const user_id = localStorage.getItem("userId");
  const [dates, setDates] = useState([null, null]);
  const [reportrange, setReportrange] = useState("");
  const [salesRapOptions, setSalesRapOptions] = useState([]);
  const [salesRap, setSalesRap] = useState("");
  const [locationReport, setLocationReport] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
  };

  const searchFormData = () => {
    let startDate = dates[0] ? format(dates[0], "dd/MM/yyyy") : "";
    let endDate = dates[1] ? format(dates[1], "dd/MM/yyyy") : "";
    setReportrange(startDate ? startDate + " - " + endDate : "");
    setPage(0);
  };

  const handleSalesRepChange = (event, newValue) => {
    setSelectedSalesRep(newValue);
    setSalesRap(newValue ? newValue.value : '');
    setPage(0);
  };

  const fetchSalesRapOptions = async () => {
    const apiSalesRapUrl = `${config.API_URL}/getSalesRep`;
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const body = new FormData();
      if (useEncrypt) {
        const ep = encryptPayload({});
        if (ep) {
          body.append('encrypted_payload', ep);
        }
      }
      const response = await fetch(apiSalesRapUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: body.entries().next().done ? undefined : body,
      });

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const formattedOptions = (data.data || []).map((item) => ({
        label: `${item.first_name} ${item.last_name}`,
        value: item.id,
      }));
      setSalesRapOptions(formattedOptions);
    } catch (error) {
    }
  };

  const getOrderLocationReportList = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: '',
        reportrange,
        sales_rep_id: salesRap || '',
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/lat_long_report_data`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setLocationReport(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);
      } else {
        setLocationReport([]);
        setTotalCount(0);
      }
    } catch (error) {
      setLocationReport([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, rowsPerPage, reportrange, salesRap]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSalesRapOptions();
  }, []);

  useEffect(() => {
    if (reportrange) {
      getOrderLocationReportList();
    }
  }, [reportrange, page, rowsPerPage, salesRap, getOrderLocationReportList]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const headers = [{ label: 'Date', width: '80px'}, { label: 'Time', width: '60px'}, { label: 'Order ID', width: '70px'}, { label: 'Customer', width: '200px'}, { label: 'Sales Rep', width: '120px'}, { label: 'Latitude', width: '100px'}, { label: 'Longitude', width: '100px'}, { label: 'Address', width: '220px'}];

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={1} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Typography className="page-title">
                  Order Location Report
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  onChange={handleDateChange}
                  style={{ width: "100%" }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                <Autocomplete
                  className="input-border"
                  size="small"
                  options={salesRapOptions}
                  value={selectedSalesRep}
                  onChange={handleSalesRepChange}
                  getOptionLabel={(option) => option.label || ''}
            
                  renderInput={(params) => (
                    <TextField 
                    {...params} 
                    label="Sales Rep" 
                    placeholder="Select Sales Rep"
                    className="text-field"
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 1 }} alignContent="end">
                <Button
                  className="button-variant-contained"
                  variant="contained"
                  onClick={searchFormData}
                  fullWidth
                  >
                  Submit
                </Button>
              </Grid2>
            </Grid2>
          </Box>

          <TableContainer>
            <Table size="medium" className="table-cell-border" sx={{ tableLayout: "fixed"}}>
              <TableHead>
                <TableRow className="table-cell-header">
                  {headers.map((header, index) => (
                    <TableCell
                      key={index}
                      sx={{
                        width: header.width,
                    
                      }}
                    >
                      {header.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={headers.length} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : locationReport.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headers.length} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  locationReport.map((row, rowIndex) => (
                    <TableRow key={rowIndex} hover
                     className="table-cell-style">
                      <TableCell>
                        {row.order_date || ''}
                      </TableCell>
                      <TableCell>
                        {row.time || ''}
                      </TableCell>
                      <TableCell>
                        {row.order_id || ''}
                      </TableCell>
                      <TableCell>
                        {row.customer || ''}
                      </TableCell>
                      <TableCell>
                        {row.sales_rep || ''}
                      </TableCell>
                      <TableCell>
                        {row.lat || ''}
                      </TableCell>
                      <TableCell>
                        {row.long || ''}
                      </TableCell>
                      <TableCell >
                        <Tooltip title={row.address || ''} placement="top">
                          <span>{row.address || ''}</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            className="pagination-styles" 
          />
        </Paper >
      </Box >

    </>
  );
};
export default OrderLocationReport;
